/**
 * @type {number}
 */
let idCounter = 0;

/**
 * Generate a unique id for an element.
 *
 * @param {string} [prefix]
 * @returns {string}
 */
export function uniqueId(prefix = '') {
  return (prefix ? `${prefix}-` : '') + (++idCounter);
}

/**
 * @param {string} selector
 * @param {HTMLElement|Document} [context]
 * @returns {?HTMLElement}
 */
export function qs(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * @param {string} selector
 * @param {HTMLElement|Document} [context]
 * @returns {HTMLElement[]}
 */
export function qsa(selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
}

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
export function inlineSize(element) {
  return element.getBoundingClientRect().width;
}

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
export function blockSize(element) {
  return element.getBoundingClientRect().height;
}

/**
 * @param {'no-preference'|'light'|'dark'} scheme
 * @returns {boolean}
 */
export function prefersColorScheme(scheme) {
  return matchMedia(`(prefers-color-scheme: ${scheme})`).matches;
}

/**
 *
 * @param {'no-preference'|'more'|'less'|'custom'} mode
 * @returns {boolean}
 */
export function prefersContrast(mode) {
  return matchMedia(`(prefers-contrast: ${mode})`).matches;
}

/**
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @returns {boolean}
 */
export function prefersReducedData() {
  return matchMedia('(prefers-reduced-data: reduce)').matches;
}

/**
 * @param {Element} element
 * @returns {Promise<PromiseSettledResult<Animation>[]>}
 */
export async function transitionsEnded(element) {
  return animationsEnded(element);
}

/**
 * @param {Element} element
 * @returns {Promise<PromiseSettledResult<Animation>[]>}
 */
export async function animationsEnded(element) {
  return Promise.allSettled(
    element.getAnimations().map((animation) => animation.finished)
  );
}

/**
 * Force a CSS animation to restart so browsers re-evaluate all animation
 * properties, including those fed by CSS custom properties.
 *
 * @param {HTMLElement} element
 * @returns {void}
 */
export function restartAnimation(element) {
  element.style.setProperty('animation-name', 'none');
  void element.getBoundingClientRect();
  element.style.removeProperty('animation-name');
}

/**
 * @template TReturnValue
 * @param {string} selector
 * @param {(elements: HTMLElement[]) => TReturnValue} callback
 * @param {() => TReturnValue} [fallback]
 * @returns {?TReturnValue}
 */
export function whenFound(selector, callback, fallback = null) {
  const elements = qsa(selector);

  if (elements.length > 0) {
    return callback(elements);
  }

  return fallback?.();
};